import { UploadOutlined } from "@ant-design/icons";
import { App, Button, Modal, Space, Tag, Upload } from "antd";
import type { UploadFile } from "antd";
import { useState } from "react";
import { confirmImport, getTemplateUrl, previewImport, type ImportPreviewResult } from "../../api/importApi";
import { PersistedTable } from "./PersistedTable";

export function ExcelImportModal({ open, onClose, module = "daily-entry", onImported }: { open: boolean; onClose: () => void; module?: string; onImported?: () => void }) {
  const { message } = App.useApp();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);

  const runPreview = async () => {
    const file = files[0]?.originFileObj;
    if (!file) return message.warning("Select an .xlsx file first");
    setLoading(true);
    try {
      const result = await previewImport(module, file);
      setPreview(result);
      message.success("Import preview ready");
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? "Unable to preview import");
    } finally {
      setLoading(false);
    }
  };

  const runConfirm = async () => {
    if (!preview?.previewToken) return message.warning("Preview token missing");
    setLoading(true);
    try {
      await confirmImport(module, preview.previewToken);
      message.success("Import completed");
      onImported?.();
      onClose();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? "Unable to confirm import");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Import Excel"
      open={open}
      onCancel={onClose}
      confirmLoading={loading}
      onOk={preview?.previewToken ? runConfirm : runPreview}
      okText={preview?.previewToken ? "Confirm Import" : "Preview Import"}
      width={760}
    >
      <Upload.Dragger
        beforeUpload={() => false}
        accept=".xlsx"
        maxCount={1}
        fileList={files}
        onChange={({ fileList }) => {
          setFiles(fileList);
          setPreview(null);
        }}
      >
        <p className="ant-upload-drag-icon"><UploadOutlined /></p>
        <p className="ant-upload-text">Select or drop an accounting import .xlsx file</p>
      </Upload.Dragger>
      <Space className="import-toolbar">
        <Button type="link" size="small" href={getTemplateUrl(module)} target="_blank">Download current module template</Button>
        {preview ? (
          <>
            <Tag color={(preview.invalidRows ?? 0) > 0 ? "red" : "green"}>{preview.validRows ?? 0} valid</Tag>
            <Tag>{preview.invalidRows ?? 0} invalid</Tag>
            <Tag>{preview.totalRows ?? preview.rows?.length ?? 0} rows</Tag>
          </>
        ) : null}
      </Space>
      {preview?.rows ? (
        <PersistedTable
          tableId={`excel-import-preview-${module}`}
          size="small"
          rowKey={(_, index) => String(index)}
          dataSource={preview.rows.slice(0, 8) as any[]}
          columns={Object.keys((preview.rows[0] as Record<string, unknown>) ?? {}).slice(0, 6).map((key) => ({ title: key, dataIndex: key }))}
          pagination={false}
          scroll={{ x: true }}
        />
      ) : null}
    </Modal>
  );
}
